import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Receipt, FileText, Boxes, Banknote, FilterX } from "lucide-react";
import api from "@/lib/api";
import { formatINRShort } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { ExecDrilldown } from "@/components/mis/ExecDrilldown";

const AXIS = { fontSize: 10, fill: "#94a3b8" };
const TIP = { fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" };
const ALL = "All";

function F({ label, value, onChange, options, testId, width = "w-[130px]" }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`h-8 text-xs ${width}`} data-testid={testId}>
        <span className="text-slate-400 mr-1">{label}:</span><SelectValue />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        <SelectItem value={ALL}>All</SelectItem>
        {(options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function Kpi({ label, value, icon: Icon, testid }) {
  return (
    <Card className="p-4 shadow-none border-slate-200/80" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-slate-400">
        <Icon className="h-3.5 w-3.5" />
        <p className="text-[11px] font-semibold uppercase tracking-wider">{label}</p>
      </div>
      <p className="font-heading text-2xl font-bold text-slate-900 mt-1">{value}</p>
    </Card>
  );
}

function Chart({ title, data, testid, angle = 0, height = 190 }) {
  return (
    <Card className="p-4 shadow-none border-slate-200/80" data-testid={testid}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-3">{title}</p>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
          <XAxis dataKey="name" tick={AXIS} axisLine={false} tickLine={false} interval={0}
            angle={angle} height={angle ? 52 : 24} textAnchor={angle ? "end" : "middle"} />
          <YAxis tick={AXIS} axisLine={false} tickLine={false} width={54} tickFormatter={(v) => formatINRShort(v)} />
          <Tooltip contentStyle={TIP} formatter={(v) => formatINRShort(v)} />
          <Bar dataKey="value" name="Invoiced" fill="#a3231b" radius={[3, 3, 0, 0]} maxBarSize={30} />
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
}

function TargetBar({ pct }) {
  const capped = Math.min(100, pct || 0);
  return (
    <div className="flex items-center gap-2 min-w-[110px]">
      <div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${pct >= 100 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-[#a3231b]"}`}
          style={{ width: `${capped}%` }} />
      </div>
      <span className="font-mono text-[11px] text-slate-500 w-11 text-right">{(pct || 0).toFixed(1)}%</span>
    </div>
  );
}

const STATUS_CHIP = {
  on_track: ["On track", "bg-emerald-100 text-emerald-700"],
  at_risk: ["At risk", "bg-amber-100 text-amber-700"],
  behind: ["Behind", "bg-red-100 text-red-700"],
  vacant: ["Vacant", "bg-slate-100 text-slate-400"],
};

function StatusChip({ status }) {
  const [label, cls] = STATUS_CHIP[status] || STATUS_CHIP.vacant;
  return <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold whitespace-nowrap ${cls}`}>{label}</span>;
}

function RollupTable({ title, rows, testid }) {
  return (
    <Card className="p-4 shadow-none border-slate-200/80" data-testid={testid}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">{title}</p>
      <div className="space-y-2">
        {(rows || []).map((r) => (
          <div key={r.name} className="grid grid-cols-[1fr_auto_auto] items-center gap-2 text-xs">
            <span className="text-slate-600 truncate">{r.name}</span>
            <span className="font-mono text-slate-400 whitespace-nowrap">{r.achieved_cr.toFixed(2)} / {r.target_cr.toFixed(2)}</span>
            <TargetBar pct={r.pct} />
          </div>
        ))}
      </div>
    </Card>
  );
}

export function InvoicedSalesSection() {
  const [f, setF] = useState({ fy: ALL, month: ALL, segment: ALL, zone: ALL, branch: ALL, executive: ALL, product_type: ALL });
  const [drill, setDrill] = useState(null);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const params = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v === ALL ? "" : v]));
  const { data, isLoading } = useQuery({
    queryKey: ["invoiced-analytics", f],
    queryFn: async () => (await api.get("/billing/invoiced-analytics", { params })).data,
    keepPreviousData: true,
  });
  const dirty = Object.values(f).some((v) => v !== ALL);
  const o = data?.options || {};
  const t = data?.totals || {};
  const monthly = (data?.monthly || []).map((m) => ({ ...m, name: m.name.slice(2) }));

  return (
    <div className="mb-7" data-testid="invoiced-sales-section">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <F label="Year" value={f.fy} onChange={set("fy")} options={o.fys} testId="inv-filter-year" width="w-[120px]" />
        <F label="Month" value={f.month} onChange={set("month")} options={o.months} testId="inv-filter-month" width="w-[130px]" />
        <F label="Segment" value={f.segment} onChange={set("segment")} options={o.segments} testId="inv-filter-segment" width="w-[125px]" />
        <F label="Zone" value={f.zone} onChange={set("zone")} options={o.zones} testId="inv-filter-zone" width="w-[135px]" />
        <F label="Branch" value={f.branch} onChange={set("branch")} options={o.branches} testId="inv-filter-branch" width="w-[140px]" />
        <F label="Executive" value={f.executive} onChange={set("executive")} options={o.executives} testId="inv-filter-executive" width="w-[150px]" />
        <F label="Product Type" value={f.product_type} onChange={set("product_type")} options={o.product_types} testId="inv-filter-product" width="w-[160px]" />
        {dirty && (
          <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs text-slate-500"
            onClick={() => setF({ fy: ALL, month: ALL, segment: ALL, zone: ALL, branch: ALL, executive: ALL, product_type: ALL })}
            data-testid="inv-filter-reset">
            <FilterX className="h-3.5 w-3.5" /> Reset
          </Button>
        )}
      </div>

      {isLoading && !data ? (
        <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Kpi label="Invoiced Value" value={formatINRShort(t.invoiced || 0)} icon={Banknote} testid="inv-kpi-value" />
            <Kpi label="Invoices" value={t.invoices || 0} icon={Receipt} testid="inv-kpi-invoices" />
            <Kpi label="Avg / Invoice" value={t.invoices ? formatINRShort((t.invoiced || 0) / t.invoices) : "—"} icon={FileText} testid="inv-kpi-avg" />
            <Kpi label="Quantity" value={(t.qty || 0).toLocaleString("en-IN")} icon={Boxes} testid="inv-kpi-qty" />
          </div>
          <div className="grid lg:grid-cols-2 gap-4 mb-4">
            <Chart title="Invoiced by Month" data={monthly} testid="inv-chart-monthly" />
            <Chart title="Invoiced by Zone" data={data?.by_zone || []} testid="inv-chart-zone" angle={-15} />
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            <Chart title="Top Executives" data={data?.by_executive || []} testid="inv-chart-executive" angle={-25} height={210} />
            <Chart title="Top Product Types" data={data?.by_product || []} testid="inv-chart-product" angle={-25} height={210} />
          </div>

          {data?.targets && (
            <div className="mt-5" data-testid="inv-target-section">
              <div className="flex flex-wrap items-baseline gap-3 mb-3">
                <p className="text-sm font-heading font-semibold text-slate-800">Target vs Achieved — FY 2026-27</p>
                <span className="font-mono text-xs text-slate-500" data-testid="inv-target-total">
                  {data.targets.total.achieved_cr.toFixed(2)} / {data.targets.total.target_cr.toFixed(2)} · {data.targets.total.pct.toFixed(1)}%
                </span>
                <span className="text-[11px] text-slate-400" data-testid="inv-target-runrate">
                  {data.targets.months_elapsed} months in · expected {data.targets.total.expected_cr.toFixed(2)} ·
                  run-rate <b className={data.targets.total.run_rate_pct >= 100 ? "text-emerald-600" : "text-amber-600"}>{data.targets.total.run_rate_pct.toFixed(1)}%</b>
                  {data.targets.booked_as_on ? <> · booked {data.targets.total.booked_cr.toFixed(2)} (Milestone as on {data.targets.booked_as_on})</> : null}
                  {" "}· alerts below {data.targets.alert_threshold_pct}%
                </span>
              </div>
              <div className="grid lg:grid-cols-[1.7fr_1fr] gap-4 items-start">
                <div className="space-y-4">
                  <Card className="shadow-none border-slate-200/80 overflow-hidden" data-testid="inv-target-table">
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                            {["Executive", "Zone", "Branch", "Target (Cr)", "Expected", "Achieved", "Booked", "Run-rate", "Status"].map((h) => (
                              <th key={h} className="px-3 py-2 whitespace-nowrap">{h}</th>))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {data.targets.rows.map((r, i) => (
                            <tr key={i}
                              className={r.status === "vacant" ? "text-slate-400" : "cursor-pointer hover:bg-slate-50 transition-colors"}
                              onClick={() => r.executive_key && setDrill(r)}
                              title={r.executive_key ? "Click to see invoices & orders" : undefined}
                              data-testid={`inv-target-row-${i}`}>
                              <td className="px-3 py-1.5 font-medium whitespace-nowrap">{r.executive}<span className="text-slate-400 font-normal"> · {r.segment}</span></td>
                              <td className="px-3 py-1.5 text-slate-500">{r.zone || "—"}</td>
                              <td className="px-3 py-1.5 text-slate-500">{r.branch || "—"}</td>
                              <td className="px-3 py-1.5 font-mono text-right">{r.target_cr.toFixed(2)}</td>
                              <td className="px-3 py-1.5 font-mono text-right text-slate-500">{r.expected_cr.toFixed(2)}</td>
                              <td className="px-3 py-1.5 font-mono text-right">{r.achieved_cr.toFixed(2)}</td>
                              <td className="px-3 py-1.5 font-mono text-right text-slate-500">{r.booked_cr.toFixed(2)}</td>
                              <td className="px-3 py-1.5"><TargetBar pct={r.run_rate_pct} /></td>
                              <td className="px-3 py-1.5"><StatusChip status={r.status} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                  <Card className="p-4 shadow-none border-slate-200/80" data-testid="inv-target-pace">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-3">Cumulative pace — expected vs achieved (Cr)</p>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={(data.targets.pace || []).map((p) => ({ ...p, name: p.month.slice(2) }))} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis dataKey="name" tick={AXIS} axisLine={false} tickLine={false} />
                        <YAxis tick={AXIS} axisLine={false} tickLine={false} width={40} />
                        <Tooltip contentStyle={TIP} />
                        <Bar dataKey="expected_cum" name="Expected" fill="#cbd5e1" radius={[3, 3, 0, 0]} maxBarSize={26} />
                        <Bar dataKey="achieved_cum" name="Achieved" fill="#a3231b" radius={[3, 3, 0, 0]} maxBarSize={26} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                </div>
                <div className="space-y-4">
                  <RollupTable title="Zone rollup (Cr)" rows={data.targets.by_zone} testid="inv-target-zone" />
                  <RollupTable title="Branch rollup (Cr)" rows={data.targets.by_branch} testid="inv-target-branch" />
                </div>
              </div>
              {drill && <ExecDrilldown row={drill} onClose={() => setDrill(null)} />}
            </div>
          )}
        </>
      )}
    </div>
  );
}
